import { useState, useEffect, createContext, useContext, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificheContext = createContext();

// Tone DI conferma: due note rapide (Web Audio API, nessun file esterno)
function playNotificationSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const playNote = (freq, start, dur, vol = 0.18) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    playNote(880, 0, 0.18);    // A5
    playNote(1175, 0.12, 0.22); // D6
  } catch (e) {
    // Suono best-effort: il browser può bloccare l'audio prima di un gesto utente
    console.debug('Suono notifica non riprodotto:', e);
  }
}

export function NotificheProvider({ children }) {
  const [notifiche, setNotifiche] = useState([]);
  const [countNonLette, setCountNonLette] = useState(0);
  const [loading, setLoading] = useState(false);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const fetchNotifiche = async () => {
    try {
      const [notificheRes, countRes] = await Promise.all([
        axios.get(`${API}/notifiche`),
        axios.get(`${API}/notifiche/count`)
      ]);
      const list = notificheRes.data || [];
      setNotifiche(list);
      setCountNonLette(countRes.data.count);

      // Rileva nuove notifiche dopo il primo poll (evita di "annunciare" quelle vecchie al login)
      if (initializedRef.current) {
        const nuove = list.filter(n => !seenIdsRef.current.has(n.id) && !n.letta);
        if (nuove.length > 0) {
          // Trigger suono se almeno una nuova non letta
          playNotificationSound();
          // Mostra toast prominente per le accettazioni (priorita massima)
          nuove.forEach(n => {
            if (n.tipo === 'preventivo_accettato') {
              toast.success(n.titolo, {
                description: n.messaggio,
                duration: 8000,
                position: 'top-right',
              });
            } else if (nuove.length === 1) {
              toast(n.titolo, { description: n.messaggio, duration: 5000 });
            }
          });
        }
      } else {
        initializedRef.current = true;
      }
      // Aggiorna set degli ID visti
      list.forEach(n => seenIdsRef.current.add(n.id));
    } catch (error) {
      console.error('Error fetching notifiche:', error);
    }
  };

  const segnaLetta = async (notificaId) => {
    try {
      await axios.put(`${API}/notifiche/${notificaId}/letta`);
      fetchNotifiche();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const segnaTutteLette = async () => {
    try {
      await axios.put(`${API}/notifiche/leggi-tutte`);
      fetchNotifiche();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const eliminaNotifica = async (notificaId) => {
    try {
      await axios.delete(`${API}/notifiche/${notificaId}`);
      fetchNotifiche();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Poll for new notifications every 15 seconds (real-time accettazioni preventivi)
  useEffect(() => {
    fetchNotifiche();
    const interval = setInterval(fetchNotifiche, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <NotificheContext.Provider value={{
      notifiche,
      countNonLette,
      loading,
      fetchNotifiche,
      segnaLetta,
      segnaTutteLette,
      eliminaNotifica
    }}>
      {children}
    </NotificheContext.Provider>
  );
}

export function useNotifiche() {
  const context = useContext(NotificheContext);
  if (!context) {
    throw new Error('useNotifiche must be used within NotificheProvider');
  }
  return context;
}
